import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, Gift, History, Wrench, ExternalLink } from 'lucide-react';
import FulfillOrdersBoard from '@/components/usb/FulfillOrdersBoard';
import CreateDemoForm from '@/components/usb/CreateDemoForm';
import UsbHistoryTable from '@/components/usb/UsbHistoryTable';
import UsbOrdersPanel from '@/components/UsbOrdersPanel';
import BlmCacheTile from '@/components/usb/BlmCacheTile';

/**
 * UX1A · USB Fulfillment Hub.
 *
 * Three-section nav: Fulfill Orders · Create Demo · USB History.
 * Advanced Support (existing UsbOrdersPanel + Sealed USB Hub + Compatibility)
 * is accessible from the top-right link — ordinary successful orders never
 * need to visit it.
 *
 * Inventory is DEFERRED to UX1D per operator directive.
 */
const TABS = [
  { id: 'fulfill', label: 'Fulfill Orders', icon: Package, testid: 'ux1a-tab-fulfill' },
  { id: 'demo',    label: 'Create Demo',    icon: Gift,    testid: 'ux1a-tab-demo'    },
  { id: 'history', label: 'USB History',    icon: History, testid: 'ux1a-tab-history' },
];

const UsbFulfillmentHub = () => {
  const [tab, setTab] = useState('fulfill');
  const [showAdvanced, setShowAdvanced] = useState(false);
  // When the operator clicks "Prepare USB" on a Fulfill card, open the
  // existing OrderDrawer via the legacy UsbOrdersPanel. We don't rebuild
  // Authorize/Ship/QC — we just route the operator into them.
  const [orderToOpen, setOrderToOpen] = useState(null);

  const handleOpenOrder = (order) => {
    setOrderToOpen(order.order_id);
    setShowAdvanced(true);  // the OrderDrawer lives inside UsbOrdersPanel
  };

  return (
    <div data-testid="usb-fulfillment-hub" style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0, color: '#0F1B2D' }}>USBs</h1>
          <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>
            Fulfill paid orders, create free demos, and search history. The
            backend state machine, serials, vault, build pipeline and QC are
            unchanged — this is a business-language layer on top.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            data-testid="ux1a-advanced-toggle"
            onClick={() => setShowAdvanced((s) => !s)}
            style={{
              padding: '6px 12px', fontSize: 12, fontWeight: 600,
              background: showAdvanced ? '#0F1B2D' : '#fff',
              color: showAdvanced ? '#fff' : '#0F1B2D',
              border: '1px solid #0F1B2D', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            <Wrench size={12} /> Advanced Support
          </button>
          <Link to="/admin/sealed-usb" target="_blank"
                data-testid="ux1a-sealed-usb-link"
                style={{
                  padding: '6px 12px', fontSize: 12, fontWeight: 600,
                  background: '#fff', color: '#4B5563',
                  border: '1px solid #D1D5DB', textDecoration: 'none',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}>
            <ExternalLink size={12} /> Sealed USB Hub
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div data-testid="ux1a-tabs" style={{
        display: 'flex', gap: 2, borderBottom: '2px solid #E5E7EB', marginBottom: 24,
      }}>
        {TABS.map(({ id, label, icon: Icon, testid }) => {
          const active = tab === id;
          return (
            <button key={id}
                    data-testid={testid}
                    onClick={() => setTab(id)}
                    style={{
                      padding: '10px 20px', fontSize: 13, fontWeight: 700,
                      background: active ? '#fff' : 'transparent',
                      color: active ? '#002FA7' : '#6B7280',
                      border: 'none',
                      borderBottom: `3px solid ${active ? '#002FA7' : 'transparent'}`,
                      marginBottom: -2, cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                    }}>
              <Icon size={14} /> {label}
            </button>
          );
        })}
      </div>

      {/* Panels */}
      <div data-testid={`ux1a-panel-${tab}`}>
        {tab === 'fulfill' && (
          <>
            <BlmCacheTile />
            <FulfillOrdersBoard onOpenOrder={handleOpenOrder} />
          </>
        )}
        {tab === 'demo' && (
          <>
            <h2 style={{ fontSize: 18, color: '#0F1B2D', marginTop: 0 }}>Create Free Demo USB</h2>
            <CreateDemoForm onCreated={() => setTab('history')} />
          </>
        )}
        {tab === 'history' && <UsbHistoryTable />}
      </div>

      {/* Advanced Support — hosts existing UsbOrdersPanel (drawer, authorize,
          QC, ship, hold/release, rebuild, reveal PIN). Nothing is deleted. */}
      {showAdvanced && (
        <div data-testid="ux1a-advanced-support-section" style={{
          marginTop: 32, borderTop: '2px dashed #D1D5DB', paddingTop: 20,
        }}>
          <div style={{
            background: '#FEF3C7', border: '1px solid #FDE68A',
            padding: 10, marginBottom: 14, fontSize: 12, color: '#92400E',
          }}>
            <strong>Advanced Support.</strong> Raw status transitions, SHA / R2 /
            manifest details, rebuild artifacts, reveal PIN, and the full
            technical dashboard. An ordinary successful order should not require
            this section.
          </div>
          <UsbOrdersPanel initialOrderId={orderToOpen} />
        </div>
      )}
    </div>
  );
};

export default UsbFulfillmentHub;
